import type { FunctionComponent, TargetedMouseEvent } from 'preact';

import { vehicleLandingFacetField } from 'Addons/fitmentSearch/vehicleLandingFacetField';
import type { VehicleWidgetTemplateParams } from 'Components/fitmentSearch/vehicleWidget/vehicleWidget';
import type { RepeaterFunctionInvoker, TemplateFunction, TemplateFunctionInvoker } from 'Components/types';
import type { FacetValueFull, SelectionEntry } from 'Models/index';
import productConfig from 'Models/uiConfig/productConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import { getRequestFromUri, getUriFromRequest } from 'Modules/converter/index';
import { cloneSafe } from 'Utils/components';

import Subtile from './subtile';

const { platformRoute } = requestConfig;

export interface SubvalueParams {
  value: string;
  href?: string;
  getImageOrDefault: (imageUrl: string) => string;
  hasSubvalues?: boolean;
  isMultiLevel?: boolean;
  vehicleWidget?: TemplateFunctionInvoker<VehicleWidgetTemplateParams>;
}

export interface Params extends SubvalueParams {
  currentTab: string | null;
  subvalues: RepeaterFunctionInvoker<SubvalueParams>;
  setCurrentTab: (tile: string) => void;
  isMultiLevel?: boolean;
}

type TileFacetSlice = Pick<FacetValueFull, 'value' | 'term' | 'isSelected'>;

type Props = {
  template: TemplateFunction<Params>;
  value: FacetValueFull;
  subvalues: FacetValueFull[];
  currentTab: string | null;
  isMultiLevel?: boolean;
  toggleFacet: (term: string) => void;
  useFilterMode?: boolean;
  vehicleFromUriSelection: SelectionEntry[];
  fitmentFields: string[];
  vehicleWidget: TemplateFunctionInvoker<VehicleWidgetTemplateParams>;
  setCurrentTab: (tile: string) => void;
};

const Tile: FunctionComponent<Props> = ({
  template,
  value,
  subvalues,
  currentTab,
  isMultiLevel,
  toggleFacet,
  useFilterMode,
  vehicleFromUriSelection,
  fitmentFields,
  vehicleWidget,
  setCurrentTab,
}) => {
  const hasSubvalues = subvalues.length > 0;

  const subvaluesMap: RepeaterFunctionInvoker<SubvalueParams> = subvalues.map((subvalue) => (templ) => {
    const props = {
      template: templ,
      toggleFacet,
      useFilterMode,
      subvalue,
      hasSubvalues,
      vehicleFromUriSelection,
      fitmentFields,
      vehicleWidget,
    };

    return <Subtile {...props} key={subvalue.term} />;
  });

  const component = template.call({
    ...getParams(value, vehicleFromUriSelection, fitmentFields),
    subvalues: subvaluesMap,
    hasSubvalues,
    currentTab,
    isMultiLevel,
    getImageOrDefault,
    setCurrentTab,
    vehicleWidget,
  });

  const options = useFilterMode
    ? {
        onClick: (e: TargetedMouseEvent<HTMLElement>) => {
          e.preventDefault();
          toggleFacet(value.term);
        },
      }
    : {};

  return cloneSafe(component, null, { ...options });
};

export function getImageOrDefault(imageUrl: string): string {
  return imageUrl || productConfig.noImageSrc;
}

export function getParams(
  value: FacetValueFull,
  vehicleFromUriSelection?: SelectionEntry[],
  fitmentFields?: string[],
): TileFacetSlice & { imageUrl: FacetValueFull['imageUrl']; href: string | undefined; payload: string } {
  const { imageUrl, isSelected, payload, term, value: facetValue } = value;

  return {
    value: facetValue,
    term,
    isSelected,
    href: getHref(value, vehicleFromUriSelection, fitmentFields),
    imageUrl,
    payload: payload || '',
  };
}

function getHref(
  value: FacetValueFull,
  vehicleFromUriSelection: SelectionEntry[] = [],
  fitmentFields?: string[],
): string {
  let url = value.url || '';

  // If value url is not provided in the index extra,
  // we may add selection in server plugins in the url as raw search params.
  const searchParamUrl = url.startsWith('/?');

  if (url) {
    // https://store.com//2017-audi///suspension/ => https://store.com/2017-audi/suspension/
    // http://store.com///suspension// => http://store.com/suspension/
    // /suspension/// => /suspension/
    // store.com//2017-audi//suspension => store.com/2017-audi/suspension
    url = url.includes('/')
      ? url.replace(/([^:])\/{2,}/g, '$1/').replace(/\/+$/g, '/')
      : `${window.location.href.replace(/\/$/, '')}/${url}`
          .replace(/([^:])\/{2,}/g, '$1/')
          .replace(/\/+$/g, '/');

    if (!searchParamUrl && platformRoute && !url.startsWith(platformRoute)) {
      url = `${platformRoute}${url}`;
    }
  }

  const parsedUrl = new URL(url, window.location.origin);

  const valueSearchSelection: SelectionEntry[] = [];
  const valueVehicleSelection: SelectionEntry[] = [];

  for (const selection of getRequestFromUri(parsedUrl).selection) {
    const isVehicleSelection =
      selection.field === vehicleLandingFacetField || fitmentFields?.includes(selection.field);

    if (isVehicleSelection) {
      valueVehicleSelection.push(selection);
    } else {
      valueSearchSelection.push(selection);
    }
  }

  if (!url || searchParamUrl) {
    // Go to the search page with all possible selections.
    const vehicleSelection = valueVehicleSelection.length ? valueVehicleSelection : vehicleFromUriSelection;
    return getUriFromRequest(
      { selection: [value, ...valueSearchSelection, ...vehicleSelection] },
      { goToSearchPage: true },
    ).href;
  }

  const vehicleSelection = !valueVehicleSelection.length ? vehicleFromUriSelection : [];
  return getUriFromRequest(
    { selection: [...valueSearchSelection, ...vehicleSelection] },
    { pathname: parsedUrl.href },
  ).href;
}

export default Tile;
